/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

// NOTE: This entity display does not support Tabular Data at this time.
class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		this.inputID = '';
		this.placeholder = '';
		this.showAllMaterials = false;
		this.disableSpecYearSiloing = false;
		this.selectedMaterial = null;
	}

	getSelectInputHTMLID(pInputHTMLID)
	{
		return `#MATERIAL-SELECT-FOR-${pInputHTMLID}`;
	}

	getSelectFilterToggleID(pInputHTMLID)
	{
		return `#MATERIAL-SELECT-FILTER-TOGGLE-${pInputHTMLID}`;
	}

	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#MATERIAL-SELECT-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#MATERIAL-SELECT-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Temporary loading skeleton for the input element.
	 * @param {object} pInput The input object.
	 */
	addLoadingSkeleton(pInput)
	{
		if (!this.pict?.AppData?.Interactive || this.pict?.AppData?.DocumentData?.ReportData?.Locked)
		{
			return;
		}
		const tmpLoadingBox = document.createElement('div');
		tmpLoadingBox.id = `LOADING-${pInput.Macro.RawHTMLID}`;
		tmpLoadingBox.classList.add('input', 'is-skeleton');
		tmpLoadingBox.style.left = '0';
		tmpLoadingBox.style.position = 'absolute';
		tmpLoadingBox.style.opacity = '0.5';
		this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID))[0]?.parentElement?.appendChild(tmpLoadingBox);
		this.pict.ContentAssignment.addClass(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID), 'is-hidden');
	}

	/**
	 * Remove the loading skeleton from the input element.
	 * @param {object} pInput The input object.
	 */
	removeLoadingSkeleton(pInput)
	{
		const tmpLoadingBox = this.pict.ContentAssignment.getElement(`#LOADING-${pInput.Macro.RawHTMLID}`);
		if (tmpLoadingBox && tmpLoadingBox.length > 0)
		{
			tmpLoadingBox[0].remove();
		}
		this.pict.ContentAssignment.removeClass(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID), 'is-hidden');
	}

	/**
	 * Initialize the Select2 plugin for the input element.
	 */
	initializeSelect2()
	{
		if (window['jQuery'] && window['jQuery'].fn.select2)
		{
			const tmpShowAllMaterials = this.showAllMaterials;
			const tmpSelectOptions = this.pict.providers.Select2Util.getOptionsAjax('Material',
				{
					SearchFields: [ 'ItemCode', 'Name' ],
					SearchPlaceholder: this.placeholder,
					GetRecordUserFacingID: (pMaterial) => pMaterial.ItemCode || 'N/A',
					HandleSearchFilters: (pFilters) =>
					{
						// if we are showing all materials, do not apply the parent filter
						// otherwise, filter to only show top-level materials (no parent)
						if (tmpShowAllMaterials)
						{
							// always ensure we have a sort by Name ascending
							if (!pFilters.length || !pFilters.includes('FSF'))
							{
								pFilters.push('FSF~Name~ASC~0');
							}
							return pFilters;
						}
						if (pFilters.length > 0)
						{
							pFilters.unshift('FOP~0~(~0');
							pFilters.push('FCP~0~)~0');
						}
						pFilters.push('FBV~IDMaterialParent~EQ~0');
						// always ensure we have a sort by Name ascending
						if (!pFilters.length || !pFilters.includes('FSF'))
						{
							pFilters.push('FSF~Name~ASC~0');
						}
						return pFilters;
					},
				});
			// if the select2 plugin is not already initialized, initialize it now for the correct input type
			if (!window['jQuery'](this.inputID).data('select2'))
			{
				window['jQuery'](this.inputID).select2(tmpSelectOptions);
			}
		}
	}

	updateToggleFilterLink(pInputHTMLID)
	{
		// update the link toggle icon
		if (this.showAllMaterials)
		{
			const newToggleLinkIconElement = document.createElement('i');
			newToggleLinkIconElement.classList.add('fas', 'fa-toggle-on');
			this.pict.ContentAssignment.assignContent(this.getSelectFilterToggleID(pInputHTMLID) + ' > .icon', newToggleLinkIconElement.outerHTML);
		}
		else
		{
			const newToggleLinkIconElement = document.createElement('i');
			newToggleLinkIconElement.classList.add('fas', 'fa-toggle-off');
			this.pict.ContentAssignment.assignContent(this.getSelectFilterToggleID(pInputHTMLID) + ' > .icon', newToggleLinkIconElement.outerHTML);
		}
	}

	/**
	 * Toggle the material filter for the input element based on the user's selection.
	 * @param {string} pInputHTMLID The input HTML ID.
	 */
	toggleMaterialFilter(pInputHTMLID)
	{
		this.showAllMaterials = !this.showAllMaterials;
		this.updateToggleFilterLink(pInputHTMLID);
		// destroy and reinitialize the select2 plugin with the new filter settings
		window['jQuery'](this.getSelectInputHTMLID(pInputHTMLID)).data('select2').destroy();
		this.initializeSelect2();
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.addLoadingSkeleton(pInput);
		// initialize the select2 plugin
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @return {Promise<boolean>}
	 */
	async _onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector)
	{
		const tmpInputSelectElements = this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
		if (!tmpInputSelectElements || tmpInputSelectElements.length < 1)
		{
			return false;
		}
		// if we have a value (ID), go fetch the Material for display (Name, etc.)
		if (pValue)
		{
			// get the value of the selected Material and set it as the selected Material on the data object
			try
			{
				const tmpMaterial = await this.pict.providers.MaterialProvider.getMaterialByID(pValue);
				this.SelectedMaterial = tmpMaterial;
			}
			catch (e)
			{
				this.pict.log.error(`Error fetching Material with ID ${ pValue }: ${ e }`);
				this.SelectedMaterial =
				{
					IDMaterial: pValue,
					Name: 'Unkown Material',
				};
			}
			// build the prefilled value for the select2 dropdown
			const tmpOption = document.createElement('option');
			const tmpSelectElement = this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID))[0];
			tmpOption.value = this.SelectedMaterial.IDMaterial;
			tmpOption.text = `${this.SelectedMaterial.ItemCode || 'N/A'}|~|${this.SelectedMaterial.Name || ('Unnamed Material #' + this.SelectedMaterial.IDMaterial)}`;
			tmpOption.selected = true;
			tmpSelectElement.appendChild(tmpOption);
		}
		if (pInput)
		{
			this.inputID = this.getSelectInputHTMLID(pInput.Macro.RawHTMLID);
			this.showAllMaterials = pInput.PictForm?.InputExtra && typeof(pInput.PictForm?.InputExtra) === 'string' && pInput.PictForm?.InputExtra.toLowerCase().replaceAll(' ', '') === 'showallmaterials' ? true : false;
			this.updateToggleFilterLink(pInput.Macro.RawHTMLID);
			this.placeholder = pInput?.PictForm?.Placeholder || 'Select a Material by name or code...';
			this.removeLoadingSkeleton(pInput);
			this.initializeSelect2();
		}
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		const tmpInputSelectValue = this.pict.ContentAssignment.readContent(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpInputSelectValue);
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, tmpInputSelectValue, pHTMLSelector);
	}
}

module.exports = CustomInputHandler;
